import React, { useState, useMemo } from 'react';
import { useData } from '../../hooks/useData';
import { useToast } from '../../hooks/useToast';
import { MetricStrip } from '../common/MetricStrip';
import { EmptyState } from '../common/EmptyState';
import { Tag } from '../common/Tag';
import { fmtDateTime } from '../../utils/helpers';

function AuditModule() {
  const { state } = useData();
  const toast = useToast();
  const [filter, setFilter] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');

  const filtered = useMemo(() => {
    return state.audit.filter((a) => {
      if (userFilter !== 'all' && a.userId !== userFilter) return false;
      if (actionFilter !== 'all' && !a.action?.startsWith(actionFilter)) return false;
      if (!filter) return true;
      const q = filter.toLowerCase();
      return (a.action || '').toLowerCase().includes(q) ||
        (a.object || '').toLowerCase().includes(q) ||
        (a.detail || '').toLowerCase().includes(q);
    });
  }, [state.audit, filter, userFilter, actionFilter]);

  const exportCSV = () => {
    const headers = ['Timestamp', 'User', 'Role', 'Action', 'Object', 'Detail'];
    const rows = filtered.map((a) => {
      const u = state.users.find((x) => x.id === a.userId);
      return [a.at, u?.name || a.userId, u?.role || '—', a.action, a.object, a.detail || '']
        .map((v) => `"${(v || '').toString().replace(/"/g, '""')}"`)
        .join(',');
    });
    const blob = new Blob([headers.join(',') + '\n' + rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `arcolab_audit_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast('Audit log exported as CSV', 'ok');
  };

  const actionPrefixes = useMemo(
    () => [...new Set(state.audit.map((a) => a.action?.split('_')[0]).filter(Boolean))],
    [state.audit]
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Section 13 · Governance</div>
          <h1 className="page-title">Audit trail</h1>
          <p className="page-sub">Tamper-evident log of every state-changing action. Per URS-AUD-001, captures user, role, action, timestamp, object and detail.</p>
        </div>
        <button className="btn" onClick={exportCSV}>
          <i className="ti ti-file-export" /> Export CSV
        </button>
      </div>

      <MetricStrip
        items={[
          { label: 'Total events', value: state.audit.length, foot: 'all time' },
          { label: 'Today', value: state.audit.filter((a) => new Date(a.at).toDateString() === new Date().toDateString()).length, foot: 'state changes' },
          { label: 'Users active', value: new Set(state.audit.map((a) => a.userId)).size - 1, foot: 'excluding system' },
          { label: 'Action types', value: actionPrefixes.length, foot: 'distinct categories' }
        ]}
      />

      <div className="section-label mt-4 mb-5">
        Event stream <span className="count">{filtered.length}</span>
      </div>

      <div className="card">
        <div className="flex gap-2" style={{ padding: '14px 18px 0' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <i className="ti ti-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-3)', fontSize: 13 }} />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search action, object, detail..."
              style={{ width: '100%', padding: '7px 10px 7px 30px', border: '1px solid var(--line)', borderRadius: 2, fontSize: 12 }}
            />
          </div>
          <select className="btn btn-sm" value={userFilter} onChange={(e) => setUserFilter(e.target.value)}>
            <option value="all">All users</option>
            {state.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select className="btn btn-sm" value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
            <option value="all">All actions</option>
            {actionPrefixes.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        {filtered.length === 0 ? (
          <EmptyState icon="ti-history-off" title="No events match" desc="Adjust filter to see audit events." />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>User</th>
                <th>Action</th>
                <th>Object</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 80).map((a) => {
                const user = state.users.find((u) => u.id === a.userId);
                return (
                  <tr key={a.id}>
                    <td className="mono xs muted" style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(a.at)}</td>
                    <td>
                      <div className="flex gap-2 center">
                        <div className="user-avatar" style={{ width: 22, height: 22, fontSize: 10, background: 'var(--surface-3)', color: 'var(--ink)' }}>
                          {user?.initials || 'SY'}
                        </div>
                        <div>
                          <div className="xs medium">{user?.name || 'System'}</div>
                          <div className="tiny muted">{user?.role || 'platform'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="mono xs"><Tag>{a.action}</Tag></td>
                    <td className="xs medium">{a.object}</td>
                    <td className="xs muted" style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.detail}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {filtered.length > 80 && (
          <div className="xs muted" style={{ padding: 12, textAlign: 'center', borderTop: '1px solid var(--line)' }}>
            Showing first 80 of {filtered.length} events. Use filters or CSV export for full view.
          </div>
        )}
      </div>
    </div>
  );
}

export default AuditModule;